import {
  type KindedNavigationElement,
  navigationElementKind,
} from "@/lib/navigation-element-kinds";

/**
 * Copy of a built-in warning page. Defaults come from the locale; the backend only
 * stores `*_override`s (`null` = use the default).
 */
export interface WarningPageCopy {
  title: string;
  description: string;
  toolbarLabel: string;
}

/** Blank or missing override falls back to the locale default. */
export function overrideOr(override: string | null | undefined, fallback: string): string {
  return override?.trim() || fallback;
}

/**
 * `null` clears the override: blank input, or input equal to the current locale default.
 * The latter keeps a saved prefilled form following the translations (and other locales).
 */
export function toOverride(value: string | undefined, fallback: string): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" || trimmed === fallback ? null : trimmed;
}

/**
 * Legacy placeholder: overrides saved before the level was appended automatically may
 * still contain it. New labels are just the prefix, e.g. "Land".
 */
export const WARNING_CODE_PLACEHOLDER = "{{code}}";

/**
 * Toolbar button text: `label` plus the highest active level (`code`, e.g. "Code oranje").
 * `join` is the locale format ("Land: Code oranje"). A legacy `{{code}}` is filled in place;
 * a blank label shows only the level.
 */
export function toolbarLabelWithCode(
  label: string,
  code: string,
  join: (label: string, code: string) => string,
): string {
  if (label.includes(WARNING_CODE_PLACEHOLDER)) {
    return label.replaceAll(WARNING_CODE_PLACEHOLDER, code);
  }
  const trimmed = label.trim();
  return trimmed ? join(trimmed, code) : code;
}

/** Maritime toolbar level: just the color name, capitalized ("geel" → "Geel"). */
export function maritimeToolbarLevel(colorName: string): string {
  return colorName.charAt(0).toUpperCase() + colorName.slice(1);
}

/** Locale nav label per navigation element `kind`. */
export type WarningPageNavDefaults = Partial<Record<string, string>>;

/** Navigation element title, else the default for its kind (`title` is nullable). */
export function warningPageNavLabel(
  element: KindedNavigationElement<{ title?: string | null }> | undefined,
  defaults: WarningPageNavDefaults,
): string {
  const kind = navigationElementKind(element);
  return element?.title?.trim() || (kind && defaults[kind]) || "";
}
